/**
 * The parameter write path — proposing changes to LIVE hardware (ADR-0014).
 *
 * Three gates, outermost first, all enforced server-side; these hooks only render
 * honestly against them:
 *
 *   1. Organisation gate  `remote_control_enabled`, default OFF. Folded into
 *                         `me.can.proposeWrites` / `me.can.approveWrites`.
 *   2. Allow-list         only nominated (device, parameter) pairs may EVER be written.
 *   3. Two-tier approval  a value inside a nominated, CITED safe band is approved at
 *                         proposal — the typed confirmation in the propose dialog IS the
 *                         ceremony. Everything else waits for a second person, who must
 *                         not be the proposer.
 *
 * TIER SEMANTICS, because the words matter downstream: `tier: 'safe'` means the value sat
 * inside the safe band at proposal and the request was born `approved`; `tier: 'gated'`
 * means it needs that second engineer and is born `pending`. A `pending` request expires
 * at `approvalExpiresAt` (24 h); an `approved` one is armed only until `sendExpiresAt`
 * (15 min) — after that it is `expired` and someone has to propose again.
 *
 * GATEWAY STATUSES ARE NOT OUTCOMES. `accepted`/`partial`/`refused`/`unresponsive` say
 * what the gateway REPLIED, not what the device DID — acceptance is not application.
 * `confirmedAt` (a telemetry read-back matching the proposed value) is the only "applied"
 * claim this product makes, and `unresponsive` means UNKNOWN: the device may well have
 * applied it, so it must never be rendered as a failure.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pollInterval } from './backoff';
import { api } from './client';

export interface SafeBand {
  min: number;
  max: number;
  /** The citation — which document says this range is safe. Never invented client-side. */
  source: string;
}

export interface AllowlistEntry {
  id: string;
  siteId: string;
  deviceId: number;
  deviceName: string;
  deviceLabel: string | null;
  metricId: number;
  baseKey: string;
  displayName: string | null;
  unit: string | null;
  safeBand: SafeBand | null;
  note: string | null;
  addedAt: string;
}

export type WriteRequestStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'cancelled'
  | 'expired'
  | 'sent'
  | 'accepted'
  | 'partial'
  | 'refused'
  | 'unresponsive';

export interface WriteRequestParameter {
  allowlistId: string;
  deviceId: number;
  deviceName: string;
  metricId: number;
  baseKey: string;
  displayName: string | null;
  unit: string | null;
  /** Proposed, in canonical units. */
  value: number;
  /** What actually goes on the wire — shown at approval so nobody arms a mystery. */
  rawValue: number;
  /** Reading at proposal time. Null when the series had never reported. */
  observed: number | null;
  observedAt: string | null;
  result?: { status: 'success' | 'error'; errorCode: number; message: string };
  appliedAt?: string;
}

export interface WriteRequestRecord {
  id: string;
  siteId: string;
  status: WriteRequestStatus;
  tier: 'safe' | 'gated';
  parameters: WriteRequestParameter[];
  reason: string;
  proposedBy: string;
  proposedByEmail: string | null;
  proposedAt: string;
  approvalExpiresAt: string;
  approvedBy: string | null;
  approvedAt: string | null;
  approvalNote: string | null;
  /**
   * Snapshot of the site's open alarms at DECISION time, taken by the server.
   * Shape: { takenAt, openCount: number, alarms: [{name, level, deviceName, openedAt}] }.
   * `unknown` because it is evidence to display, not data to compute with — render a
   * decided request from this, and only a still-pending one from the live alarms feed.
   */
  alarmContext: unknown;
  sendExpiresAt: string | null;
  rejectedBy: string | null;
  rejectReason: string | null;
  sentAt: string | null;
  completedAt: string | null;
  confirmedAt: string | null;
}

/**
 * Every field `| undefined` explicitly: under `exactOptionalPropertyTypes` clearing a
 * filter by assigning `undefined` is a different type from omitting the key, and "clear
 * this filter" is exactly what the site selector does.
 */
export interface WriteRequestFilters {
  siteId?: string | undefined;
  status?: WriteRequestStatus | undefined;
  limit?: number | undefined;
}

/* ---------------------------------------------------------------- allow-list */

export function useWriteAllowlist(siteId?: string | undefined) {
  return useQuery({
    queryKey: ['write-allowlist', siteId ?? null],
    queryFn: () =>
      api.get<{ data: AllowlistEntry[] }>('/write-allowlist', siteId ? { siteId } : undefined),
    /* Nominations change when an approver acts, not on a schedule — no polling. */
    staleTime: 60_000,
  });
}

export interface NominatePairInput {
  siteId: string;
  deviceId: number;
  metricId: number;
  safeBand?: SafeBand | undefined;
  note?: string | undefined;
}

export function useNominatePair() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ safeBand, note, ...rest }: NominatePairInput) =>
      api.post<{ entry: AllowlistEntry }>('/write-allowlist', {
        ...rest,
        ...(safeBand ? { safeBand } : {}),
        ...(note ? { note } : {}),
      }),
    onSuccess: () => {
      /* Invalidated rather than seeded: the response is one entry, and the cache holds
         per-site LISTS keyed on a filter this hook cannot see all instances of. */
      void qc.invalidateQueries({ queryKey: ['write-allowlist'] });
    },
  });
}

export function useRemoveNomination() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.del<null>(`/write-allowlist/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['write-allowlist'] });
      /* A removed nomination does not touch existing requests — the server snapshots the
         parameter identity into the request row — so ['write-requests'] stays put. */
    },
  });
}

/* ------------------------------------------------------------------ requests */

export function useWriteRequests(filters: WriteRequestFilters = {}, enabled = true) {
  return useQuery({
    queryKey: ['write-requests', filters],
    queryFn: () =>
      api.get<{ data: WriteRequestRecord[]; pending: number }>('/write-requests', {
        ...(filters.siteId ? { siteId: filters.siteId } : {}),
        ...(filters.status ? { status: filters.status } : {}),
        limit: filters.limit ?? 100,
      }),
    /*
     * Polled: a pending request is a colleague waiting on a second signature, and an
     * approved one is armed against a 15-minute clock. A screen that only updates on
     * navigation would show "approved" for a request that expired ten minutes ago.
     */
    refetchInterval: pollInterval(30_000),
    enabled,
  });
}

export function useWriteRequest(id: string | null) {
  return useQuery({
    /* Its own root, NOT ['write-requests', ...]: mutations below seed this key from their
       response and then invalidate the list root — a detail under the same root would be
       refetched in the same breath it was seeded, a round trip to learn what the server
       just said. */
    queryKey: ['write-request', id],
    queryFn: () => api.get<{ request: WriteRequestRecord }>(`/write-requests/${id}`),
    enabled: Boolean(id),
    refetchInterval: pollInterval(30_000),
  });
}

export interface ProposeWriteInput {
  siteId: string;
  /** Server floor: 10 characters. The form states it before the submit, not after. */
  reason: string;
  parameters: Array<{ deviceId: number; metricId: number; value: number }>;
}

/** Seed the detail, invalidate the lists — every decision mutation shares this shape. */
function useRequestMutation<TVars>(fn: (vars: TVars) => Promise<{ request: WriteRequestRecord }>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (res) => {
      /* The response IS the stored record; refetching it would race the badge repaint. */
      qc.setQueryData(['write-request', res.request.id], res);
      // The row's status, the pending count and the badge all read the list.
      void qc.invalidateQueries({ queryKey: ['write-requests'] });
    },
  });
}

export function useProposeWrite() {
  return useRequestMutation((input: ProposeWriteInput) =>
    api.post<{ request: WriteRequestRecord }>('/write-requests', input),
  );
}

export function useApproveWrite() {
  return useRequestMutation(({ id, note }: { id: string; note?: string | undefined }) =>
    api.post<{ request: WriteRequestRecord }>(
      `/write-requests/${id}/approve`,
      note ? { note } : {},
    ),
  );
}

export function useRejectWrite() {
  return useRequestMutation(({ id, reason }: { id: string; reason: string }) =>
    api.post<{ request: WriteRequestRecord }>(`/write-requests/${id}/reject`, { reason }),
  );
}

export function useCancelWrite() {
  return useRequestMutation((id: string) =>
    api.post<{ request: WriteRequestRecord }>(`/write-requests/${id}/cancel`),
  );
}
